import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { 
  LayoutDashboard, Users, Calendar, Dumbbell, Settings, LogOut, Menu, X, Zap, Package, ClipboardList, Video, DollarSign, UserPlus, Star, User, Gift, MessageSquare, Activity
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import BetaBanner from '@/components/BetaBanner';
import FoundingWelcome from '@/components/FoundingWelcome';
import QuickCaptureFAB from '@/components/QuickCaptureFAB';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const CoachLayout = () => {
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread message count
  useEffect(() => {
    const fetchUnreadCount = async () => {
      try {
        const token = localStorage.getItem('coach_token');
        if (!token) return;
        
        const res = await axios.get(`${API}/api/conversations`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Sum all unread counts from conversations
        const total = res.data.reduce((sum, conv) => sum + (conv.unread_count || 0), 0);
        setUnreadCount(total);
      } catch (err) {
        console.error('Failed to fetch unread count:', err);
      }
    };
    
    fetchUnreadCount();
    // Poll every 30 seconds for new messages
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/unified-login');
  };

  // Check if user has Pro plan for financial dashboard access
  const hasPro = user?.subscription?.plan === 'pro';
  
  // Check if coach is invited-only (no active subscription)
  const isInvitedOnly = user?.coach_mode === 'invited_only';

  // Full navigation for subscribed coaches
  const fullNavItems = [
    { path: '/coach', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/coach/athletes', icon: Users, label: 'Athletes' },
    { path: '/coach/sessions', icon: Calendar, label: 'Sessions' },
    { path: '/coach/calendar', icon: Calendar, label: 'Calendar' },
    { path: '/coach/training-plans', icon: ClipboardList, label: 'Training Plans' },
    { path: '/coach/drills', icon: Dumbbell, label: 'Drill Library' },
    { path: '/coach/videos', icon: Video, label: 'Videos' },
    { path: '/coach/packages', icon: Package, label: 'Packages' },
    // Financial dashboard only for Pro plan
    ...(hasPro ? [{ path: '/coach/financials', icon: DollarSign, label: 'Financials' }] : []),
    // Vouchers for Pro coaches
    ...(hasPro ? [{ path: '/coach/vouchers', icon: Gift, label: 'Vouchers', badge: 'PRO' }] : []),
    { path: '/coach/messages', icon: MessageSquare, label: 'Messages', unreadBadge: true },
    { path: '/coach/invited-athletes', icon: UserPlus, label: 'Invited Athletes' },
    { path: '/coach/settings', icon: Settings, label: 'Settings' },
  ];

  // Limited navigation for invited-only coaches (no subscription)
  const invitedOnlyNavItems = [
    { path: '/coach/invited-athletes', icon: Users, label: 'My Athletes' },
    { path: '/coach/settings', icon: Settings, label: 'Settings' },
  ];

  const navItems = isInvitedOnly ? invitedOnlyNavItems : fullNavItems;

  // Redirect invited-only coaches to invited-athletes page
  useEffect(() => {
    if (isInvitedOnly && location.pathname === '/coach') {
      navigate('/coach/invited-athletes');
    }
  }, [isInvitedOnly, location.pathname, navigate]);

  const isActive = (path) => {
    if (path === '/coach') return location.pathname === '/coach';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Founding Beta Banner */}
      <BetaBanner />
      
      <div className="flex flex-1">
        {/* Mobile sidebar toggle - positioned on right side */}
        <button 
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="lg:hidden fixed top-16 right-4 z-50 p-2.5 bg-card border border-border rounded-sm shadow-lg"
          data-testid="coach-mobile-menu-btn"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>

        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-40 top-8 lg:top-0
          w-64 bg-card border-r border-border
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            {/* Logo */}
            <div className="p-6 border-b border-border">
              <Link to="/coach" className="flex items-center gap-2">
                {theme.logoUrl ? (
                  <img src={theme.logoUrl} alt="Logo" className="h-8 w-auto" />
                ) : (
                  <img src="/brand/meybra-logo.png" alt="Meybra" className="h-8 w-auto" data-testid="brand-logo" />
                )}
              <span className="font-heading text-lg truncate">{theme.businessName}</span>
            </Link>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <BetaBanner variant="inline" />
              {/* V1: Performance Coach badge — surfaces role context so the
                  coach always knows they're in physical-only mode. */}
              {user?.coach_role === 'private_performance' && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold bg-orange-400/15 text-orange-300 border border-orange-400/40"
                  data-testid="coach-role-badge-performance"
                >
                  <Activity size={10} />
                  Performance
                </span>
              )}
            </div>
          </div>

          {/* Upgrade CTA for invited-only coaches */}
          {isInvitedOnly && (
            <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-primary/20 to-primary/10 rounded-lg border border-primary/30">
              <div className="flex items-center gap-2 mb-2">
                <Star size={16} className="text-primary" />
                <span className="text-sm font-semibold text-primary">Founding Beta — Full Access</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Build your own athlete base with complimentary access during the validation phase.
              </p>
              <Link to="/#pricing">
                <Button size="sm" className="w-full">
                  Explore Platform
                </Button>
              </Link>
            </div>
          )}

          {/* Navigation */}
          <nav className="flex-1 min-h-0 overflow-y-auto p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <div className="relative">
                  <item.icon size={20} />
                  {/* Unread message badge */}
                  {item.unreadBadge && unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 min-w-[18px] h-[18px] flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full px-1">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-sm uppercase tracking-wider">{item.label}</span>
                {item.badge && (
                  <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-primary/20 text-primary rounded">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </nav>

          {/* User section — flex-shrink-0 keeps logout always reachable on PWA */}
          <div className="p-4 border-t border-border flex-shrink-0">
            <div className="mb-4 px-4">
              <p className="text-sm font-medium truncate">{user?.full_name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              <div className="mt-2 relative group">
                <div className={`inline-flex items-center px-2 py-1 rounded text-xs uppercase cursor-help ${
                  isInvitedOnly 
                    ? 'bg-amber-500/10 text-amber-500 border border-amber-500/30' 
                    : 'bg-primary/10 text-primary'
                }`}>
                  {isInvitedOnly ? (
                    <>
                      <User size={12} className="mr-1" />
                      Athlete-Invited Coach
                    </>
                  ) : (user?.subscription?.plan || 'Starter')}
                </div>
                {/* Tooltip for Invited Coach */}
                {isInvitedOnly && (
                  <div className="absolute left-0 bottom-full mb-2 w-56 p-3 bg-card border border-border rounded-sm text-xs opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50 shadow-lg">
                    <p className="font-semibold text-amber-400 mb-1">Limited Access</p>
                    <p className="text-muted-foreground leading-relaxed">
                      You can view athlete history, add notes, and comment on videos. 
                      Subscribe to a coach plan to create training plans and manage your own athletes.
                    </p>
                  </div>
                )}
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
            >
              <LogOut size={20} />
              <span className="text-sm uppercase tracking-wider">Logout</span>
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="flex-1 lg:ml-0 min-h-screen overflow-x-hidden">
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-6">
          <Outlet />
        </div>
      </main>
      </div>
      <FoundingWelcome userId={user?.id} role="coach" />
      {!isInvitedOnly && <QuickCaptureFAB />}
    </div>
  );
};

export default CoachLayout;
